import { useCallback, useMemo } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Option } from "@/components/dropdown/Option";
import { getMatchVariants } from "@/components/expression/utils/switch-notations/getMatchVariants";
import { substituteCaptures } from "@/components/expression/utils/switch-notations/substituteCaptures";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";

import type { CapturesMap } from "@/components/expression/utils/switch-notations/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type { SwitchNotationsJsonItem, SwitchNotationVariant } from "@corca/data";

type Props = {
  doc: DocumentModel;
  notations: SwitchNotationsJsonItem[];
  nodes: NodeModel[];
};

export const SwitchNotationsList = observer(function SwitchNotationsList({
  doc,
  nodes,
  notations,
}: Props) {
  const t = useTranslations("sidebar.switchNotations");

  const matches = useMemo(() => getMatchVariants(nodes, notations), [nodes, notations]);

  const handleNotationSelect = useCallback(
    (variant: SwitchNotationVariant, captures: CapturesMap) => {
      const applied = doc.transact(() => {
        const nodesJson = substituteCaptures(variant.nodesJson, captures);
        if (!nodesJson) {
          return false;
        }

        const substitution = nodesJson
          .map((node) => createNodeFrom(node, doc, { resetIds: true }))
          .filter(Boolean);

        if (substitution.length === 0) {
          return false;
        }

        doc.input.requestSmoothScroll();

        const [first] = nodes;

        substitution.forEach((node) => first.insertSiblingBefore(node));
        for (const node of nodes) {
          doc.removeNode(node);
        }

        const { cursor, selection } = doc.cursorContext();

        cursor.moveAfterNode(substitution.at(-1));
        selection.resetSelection();

        return true;
      }, "handleNotationSelect");

      if (applied) {
        doc.input.focus();
      }
    },
    [doc, nodes],
  );

  const hasVariants = matches.some((match) => match.variants.length > 0);

  if (!hasVariants) {
    return <div className="p-3 pt-2 text-xs text-gray-500 text-center">{t("empty")}</div>;
  }

  return (
    <ul>
      {matches.flatMap((match) =>
        match.variants.map((variant, i) => (
          <Option
            key={`${match.name}:${match.index}:${variant.signature.join(",")}`}
            title={`${match.name}:${match.index + i}`}
            onClick={() => handleNotationSelect(variant, match.captures)}
          />
        )),
      )}
    </ul>
  );
});
